<template>
    <v-app>
        <md-snackbar :md-position="snackbar.position"
            :md-duration="snackbar.duration"
            :md-active.sync="snackbar.visibleSnackbar"
            :md-persistent="false">
            <span>{{ snackbar.alertMessage }}</span>
            <md-button class="md-primary"
                @click="snackbar.visibleSnackbar = false">
                {{ $t('actions.dismiss') }}
            </md-button>
        </md-snackbar>

        <md-toolbar>
            <!--Left search -->
            <div class="md-toolbar-section-start">
                <div class="legend">
                    <span class="confirmed">{{ $t('orders.confirmed') }}</span>
                    <span class="rejected">{{ $t('orders.rejected') }}</span>
                    <span class="pending">{{ $t('orders.pending') }}</span>
                    <span class="parcial">{{ $t('orders.parcial') }}</span>
                </div>
            </div>
            <!--Right actions -->
            <div class="md-toolbar-section-end">
                <md-field md-clearable
                    v-if="isAdmin">
                    <md-input :placeholder="$t('general.search-by-name')"
                        v-model="search"
                        @input="fetchOrders">
                    </md-input>
                </md-field>
                <md-field>
                    <label for="ordersstatus">{{ $t('orders.order-status') }}</label>
                    <md-select v-model="filter"
                        name="ordersstatus"
                        id="ordersstatus"
                        @input="fetchOrders">
                        <md-option value="0">
                            {{ $t('general.all') }}
                        </md-option>
                        <md-option v-for="orderstatus in ordersStatus"
                            :key="orderstatus.orderstatus_id"
                            :value="orderstatus.orderstatus_id">
                            {{ orderstatus.description }}
                        </md-option>
                    </md-select>
                </md-field>
                <md-button class="md-icon-button"
                    @click="fetchOrders">
                    <md-icon>refresh</md-icon>
                    <md-tooltip md-direction="top">
                        {{ $t('orders.refresh') }}
                    </md-tooltip>
                </md-button>
                <md-button class="md-icon-button"
                    @click="toRoute('/orders/new')">
                    <md-icon>add_circle_outline</md-icon>
                    <md-tooltip md-direction="top">
                        {{ $t('orders.new-order') }}
                    </md-tooltip>
                </md-button>
                <md-button v-if="isAdmin"
                    class="md-icon-button"
                    @click="changeToFinalized"
                    :disabled="!selected.length">
                    <md-icon>check_circle_outline</md-icon>
                    <md-tooltip md-direction="top">
                        {{ $t('general.process') }}
                    </md-tooltip>
                </md-button>
                <md-button class="md-icon-button"
                    @click="showPreview"
                    :disabled="!selected.length">
                    <md-icon>print</md-icon>
                    <md-tooltip md-direction="top">
                        {{ $t('general.print') }}
                    </md-tooltip>
                </md-button>
            </div>
        </md-toolbar>

        <div>
            <md-progress-spinner v-if="isLoading"
                md-mode="indeterminate"></md-progress-spinner>

            <v-data-table v-else
                v-model="selected"
                :headers="headers"
                :items="dataSource"
                :pagination.sync="pagination"
                select-all
                item-key="order_id"
                class="elevation-1"
                rows-per-page-text="$vuetify.dataIterator.rowsPerPageText"
                no-results-text="$vuetify.noDataText"
                no-data-text="$vuetify.noDataText"
                :rows-per-page-items="paginationAux.rowsPerPageItems"
                :total-items="paginationAux.totalItems"
                expand>
                <template v-slot:items="props">
                    <tr class="order-row"
                        :class="'orderstatus-'+props.item.orderstatus_id">
                        <td>
                            <v-checkbox v-model="props.selected"
                                primary
                                hide-details></v-checkbox>
                        </td>
                        <td class="text-xs-center">
                            {{ props.item.order_id }}
                        </td>
                        <td class="text-xs-left">
                            {{ props.item.organization_name }}
                        </td>
                        <td class="text-xs-right">
                            {{ formatDateTime(props.item.created_at) }}
                        </td>
                        <td v-if="props.item.created_at != props.item.updated_at"
                            class="text-xs-right">
                            <span v-if="props.item.changed"
                                id="updated">
                                {{ formatDateTime(props.item.updated_at) }}
                            </span>
                            <span v-else>
                                {{ formatDateTime(props.item.updated_at) }}
                            </span>
                        </td>
                        <td v-else
                            class="text-xs-right">
                            {{ "--" }}
                        </td>

                        <td class="text-xs-right">
                            {{ formatDateTime(props.item.delivery_date) }}
                        </td>
                        <td class="text-xs-right">
                            {{ formatDateTime(props.item.shipped_date) }}
                        </td>
                        <td class="text-xs-right">
                            {{ props.item.order_status }}
                        </td>
                        <td class="justify-center px-0">
                            <md-button class="md-icon-button"
                                @click="props.expanded = !props.expanded">
                                <md-icon>remove_red_eye</md-icon>
                                <md-tooltip md-direction="top">
                                    {{ $t('actions.view') }}
                                </md-tooltip>
                            </md-button>
                            <md-button class="md-icon-button"
                                @click="editOrder(props.item)"
                                :disabled="!isEditable(props.item.orderstatus_id)">
                                <md-icon>edit</md-icon>
                                <md-tooltip md-direction="top">
                                    {{ $t('actions.edit') }}
                                </md-tooltip>
                            </md-button>
                            <md-button class="md-icon-button"
                                @click="deleteOrder(props.item)"
                                :disabled="!isParcial(props.item.orderstatus_id)">
                                <md-icon>delete</md-icon>
                                <md-tooltip md-direction="top">
                                    {{ $t('actions.cancel') }}
                                </md-tooltip>
                            </md-button>
                        </td>
                    </tr>
                </template>
                <!-- EXPANDED TABLE  -->
                <template slot="expand"
                    slot-scope="props">
                    <md-table>
                        <md-table-row>
                            <md-table-head class="text-center">
                                {{ $tc('products.product', 2) }}
                            </md-table-head>
                            <md-table-head class="text-center">
                                {{ $tc('products.quantity', 1) }} e {{ $tc('units.unit', 1) }} / {{ $tc('products.caliber', 1) }}
                            </md-table-head>
                            <md-table-head class="text-center">
                                {{ $t('products.status') }}
                            </md-table-head>
                            <md-table-head class="text-center">
                                {{ $t('products.preparation') }}
                            </md-table-head>
                            <md-table-head class="text-center">
                                {{ $tc('products.item-status', 2) }}
                            </md-table-head>
                            <md-table-head class="text-center">
                                {{ $tc('products.observation', 2) }}
                            </md-table-head>
                        </md-table-row>
                        <md-table-row v-for="(product,index) in props.item.products"
                            :key="index">
                            <td>{{ product.product_name }}</td>
                            <td>{{ product.quantity }} {{ product.units }} / {{ product.caliber }}</td>
                            <td>{{ product.product_status }}</td>
                            <td>{{ product.prepared === true ? $t('general.yes') : $t('general.no') }}</td>
                            <td>{{ getOrderItemStatus(product.order_item_status_id) }}</td>
                            <td>{{ product.notes || '--' }}</td>
                        </md-table-row>
                    </md-table>
                    <md-table>
                        <md-table-row>
                            <td class="text-left">
                                <span style="font-weight: bold;color: rgba(0,0,0,0.54);">{{ $t('orders.order-notes') }}:</span> {{ props.item.description || '--' }}
                            </td>
                        </md-table-row>
                    </md-table>
                </template>
            </v-data-table>
        </div>
    </v-app>
</template>

<script>
import ordersService from '@/components/orders/services/'
import ordersStatusService from '@/components/orders/services/ordersStatus'
import organizationsService from '@/components/organizations/services/organizations'
import orderStatus from '@/utils/orderStatus'
import _ from 'lodash'
import Swal from 'sweetalert2'
import luxon from '@/mixins/luxon'

const loadingKeys = {
    fetchingItem: 'fetching-item'
}

export default {
    name: 'OrdersList',

    mixins: [luxon],

    data () {
        return {
            dataSource: [],
            selected: [],
            loadings: [],
            ordersStatus: [],
            search: '',
            filter: '0',
            showModal: false,
            viewId: null,

            expanded: [],
            singleExpand: true,

            order: {
                order_id: null,
                organization_id: this.$store.getters.user.organizationId,
                user_id: this.$store.getters.user.userId,
                delivery_date: null,
                shipped_date: null,
                description: '',
                orderstatus_id: 1,
                active: 1,
                created_at: null,
                updated_at: null,
                organization_name: '',
                first_name: '',
                last_name: '',
                order_status: 'Pendente',
                preferred_delivery_hour: null,
                address: '',
                products: []
            },

            organization: {
                organization_name: '',
                nipc: '',
                phone: '',
                email: '',
                street: '',
                city: '',
                state: '',
                zip_code: '',
                preferred_delivery_hour: null,
                organization_picture_id: '',
                logo_picture_id: '',
                organization_picture_url: '',
                logo_picture_url: ''
            },

            snackbar: {
                visibleSnackbar: false,
                position: 'center',
                duration: 3000,
                alertMessage: this.$t('validations.operation-performed-successfully')
            },

            paginationAux: {
                currentPage: 1,
                descending: true,
                page: 1,
                rowsPerPage: 10,
                sortBy: 'order_id',
                totalItems: 0,
                rowsPerPageItems: [5, 10, 15, 25, 50]
            },

            headers: [
                {
                    text: this.$t('general.id'),
                    align: 'center',
                    value: 'order_id',
                    sortable: true
                },
                {
                    text: this.$tc('organizations.organization', 1),
                    align: 'left',
                    value: 'organization_name',
                    sortable: true
                },
                {
                    text: this.$t('general.created-at'),
                    align: 'right',
                    value: 'created_at',
                    sortable: false
                },
                {
                    text: this.$t('general.updated-at'),
                    align: 'right',
                    value: 'updated_at',
                    sortable: false
                },
                {
                    text: this.$t('general.delivery-date'),
                    align: 'right',
                    value: 'delivery_date',
                    sortable: false
                },
                {
                    text: this.$t('general.shipped-date'),
                    align: 'right',
                    value: 'shipped_date',
                    sortable: false
                },
                {
                    text: this.$t('orders.order-status'),
                    align: 'right',
                    value: 'orderstatus_id',
                    sortable: false
                },
                {
                    text: this.$tc('general.action', 2),
                    align: 'center',
                    value: '',
                    sortable: false
                }
            ]
        }
    },

    computed: {
        isAdmin () {
            return this.$store.getters.user.role === 'ADMIN'
        },

        isLoading () {
            return this.isFetchingOrders
        },

        isFetchingOrders () {
            return !!this.loadings.filter(
                loading => loading.key === loadingKeys.fetchingItem
            ).length
        },

        pagination: {
            get: function () {
                return this.paginationAux
            },

            set: function (value) {
                this.paginationAux = value
                this.fetchOrders()
            }
        }
    },

    methods: {
        getOrderItemStatus (id) {
            return orderStatus.getOrderItemStatus(id)
        },

        async fetchProducts (orderId) {
            let params = {
                filter: {
                    order_id: orderId
                }
            }

            let response = await ordersService.retrieveItems(params)
            return response.data
        },

        async fetchOrderStatus () {
            let self = this

            let params = {
                filter: {
                    active: 1
                }
            }

            let response = await ordersStatusService.getOrdersStatus(params)
            self.ordersStatus = _.cloneDeep(response.data.results)
        },

        async fetchOrders () {
            _.fill(this.loadings, loadingKeys.fetchingItem)

            let self = this
            self.dataSource = []
            let parameters = {
                pagination: {
                    page: self.paginationAux.page,
                    rowsPerPage: self.paginationAux.rowsPerPage
                },
                orderBy: {
                    column: self.paginationAux.sortBy,
                    direction: self.paginationAux.descending ? 'DESC' : 'ASC'
                }
            }

            if (!self.isAdmin) {
                parameters.filter = {
                    orderstatus_id: self.filter === '0' ? _.reduce(
                        self.ordersStatus,
                        function (result, value, key) {
                            result.push(value.orderstatus_id)
                            return result
                        },
                        []
                    ) : self.filter,
                    organization_id: this.$store.getters.user.organizationId,
                    user_id: this.$store.getters.user.userId
                }
            } else {
                parameters.filter = {
                    orderstatus_id: self.filter === '0' ? _.reduce(
                        self.ordersStatus,
                        function (result, value, key) {
                            result.push(value.orderstatus_id)
                            return result
                        },
                        []
                    ) : self.filter,
                    organization_name: self.search,
                    user_id: this.$store.getters.user.userId
                }
            }

            let response = await ordersService.retrieve(parameters)

            // add products to each order so they can be presented in related expanded row
            for (const order of response.data.results) {
                let productsResponse = await self.fetchProducts(order.order_id)
                order['products'] = productsResponse
            }

            self.dataSource = _.cloneDeep(response.data.results)
            self.paginationAux.totalItems = response.data.pagination.totalItems
            self.paginationAux.rowsPerPage = response.data.pagination.rowsPerPage
            self.paginationAux.currentPage = response.data.pagination.currentPage

            _.pull(this.loadings, loadingKeys.fetchingItem)
        },

        concatOrganizationAddress (organization) {
            return (
                (organization[0].street ? organization[0].street + '\n' : '') +
                (organization[0].zip_code ? organization[0].zip_code + '\n' : '') +
                (organization[0].city ? organization[0].city + '\n' : '')
            )
        },

        makeDeliveryDateTime (deliveryDate, deliveryTime) {
            let year = deliveryDate.getFullYear()

            let month = deliveryDate.getMonth() + 1
            if (month < 10) {
                month = '0' + month
            }

            let day = deliveryDate.getDate()
            if (day < 10) {
                day = '0' + day
            }

            let deliveryHours = deliveryTime.split(':')[0]
            if (deliveryHours.length < 2) {
                deliveryHours = '0' + deliveryHours
            }

            let deliveryMinutes = deliveryTime.split(':')[1]
            if (deliveryMinutes.length < 2) {
                deliveryMinutes = '0' + deliveryMinutes
            }

            let deliveryDateTimeString = year + '-' + month + '-' + day + ' ' + deliveryHours + ':' + deliveryMinutes

            return deliveryDateTimeString
        },

        async fetchOrganization () {
            let self = this

            let params = {
                filter: {
                    organization_id: this.$store.getters.user.organizationId
                }
            }

            let response = await organizationsService.retrieve(params)
            self.organization = _.cloneDeep(response.data.results)

            self.order.organization_name = (self.organization[0].organization_name ? self.organization[0].organization_name : '')
            self.order.preferred_delivery_hour = this.formatTimeSimple(self.organization[0].preferred_delivery_hour)
            self.order.address = self.concatOrganizationAddress(self.organization)
            self.order.delivery_date = this.makeDeliveryDateTime(new Date(), self.order.preferred_delivery_hour)
            self.order.first_name = this.$store.getters.user.firstName
            self.order.last_name = this.$store.getters.user.lastName
        },

        showPreview () {
            this.$store.dispatch('printPreview', this.selected)
            this.$router.push('/orders/preview')
        },

        toRoute (destination) {
            this.$router.push(destination)
        },

        viewOrder (item) {
            this.viewId = item.order_id
            this.showModal = true
        },

        editOrder (item) {
            this.$router.push('/orders/' + item.order_id + '/edit')
        },

        deleteOrder (item) {
            Swal.fire({
                title: this.$t('validations.confirmation'),
                text: this.$t('validations.question-item-delete-confirm'),
                type: 'warning',
                showCloseButton: true,
                showCancelButton: true,
                cancelButtonText: this.$t('actions.cancel'),
                confirmButtonText: this.$t('actions.confirm'),
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.confirmDelete(item)
                }
            })
        },

        async confirmDelete (item) {
            let self = this

            let parameters = {
                order_id: item.order_id
            }
            await ordersService.rejectOrder(parameters)

            self.fetchOrders()
            self.showSnackbar()
        },

        showSnackbar () {
            this.snackbar.visibleSnackbar = true
        },

        changeSort (column) {
            if (this.pagination.sortBy === column) {
                this.pagination.descending = !this.pagination.descending
            } else {
                this.pagination.sortBy = column
                this.pagination.descending = false
            }
        },

        isEditable (value) {
            return value === 1 || value === 3
        },

        isParcial (value) {
            return value === 1
        },

        async changeToFinalized () {
            let self = this

            let selectedOrders = self.selected

            for (const order of selectedOrders) {
                if (order.orderstatus_id === 1) {
                    let parameters = {
                        order_id: order.order_id
                    }
                    await ordersService.finalizeOrder(parameters)
                }
            }

            self.fetchOrders()
        }
    },

    async created () {
        await this.fetchOrderStatus()
        await this.fetchOrders()
    }
}
</script>
